import PublicLayout from "@/components/layout/PublicLayout";
import { HOW_IT_WORKS, INDUSTRIES, FAQS, CONSULTATION_URL, resolveFaqAnswer, MOQ_DESCRIPTION_DEFAULT } from "@/data/constants";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useEffect, useState } from "react";
import api from "@/lib/api";

const DarkHero = ({ eyebrow, title, subtitle }) => (
  <section className="ft-section-dark py-24">
    <div className="ft-container max-w-3xl ft-fade-in relative">
      <div className="text-[0.6875rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-semibold mb-6">{eyebrow}</div>
      <span className="ft-gold-rule-lg" />
      <h1 className="mt-6 text-white text-[clamp(2.25rem,4.5vw,3.5rem)] font-bold leading-[1.02] tracking-tight mb-6">{title}</h1>
      {subtitle && <p className="text-white/60 text-lg leading-relaxed">{subtitle}</p>}
    </div>
  </section>
);

export const HowItWorks = () => (
  <PublicLayout>
    <DarkHero
      eyebrow="The FLOWTALIS Process"
      title={<>Six stages. <span className="ft-serif text-[var(--ft-gold)]">One operating system</span>. Every launch, on-brand and on time.</>}
      subtitle="From your first consultation call to your first reorder — every step is documented, tracked, and delivered inside the FLOWTALIS platform."
    />
    <section className="py-24 bg-white">
      <div className="ft-container space-y-4">
        {HOW_IT_WORKS.map((s) => (
          <div key={s.step} className="ft-card grid md:grid-cols-[140px_1fr] gap-6 items-center">
            <div>
              <div className="text-[0.625rem] uppercase tracking-[0.32em] text-[var(--ft-gold)] font-bold">STEP</div>
              <div className="text-4xl font-bold tracking-tighter mt-1" style={{ color: "var(--ft-black)" }}>{s.step}</div>
            </div>
            <div>
              <h3 className="text-xl font-semibold text-[var(--ft-black)]">{s.title}</h3>
              <div className="ft-gold-rule my-3" />
              <p className="text-[var(--ft-muted)] leading-relaxed">{s.desc}</p>
            </div>
          </div>
        ))}
      </div>
    </section>
    <section className="ft-section-dark py-24 text-center">
      <div className="ft-container max-w-xl relative">
        <h2 className="text-white text-3xl md:text-4xl font-bold mb-4">See the process in action.</h2>
        <p className="text-white/60 mb-8">Book a consultation and receive a full launch scope tailored to your category.</p>
        <a href={CONSULTATION_URL} target="_blank" rel="noopener noreferrer" className="ft-btn-gold">Book Consultation <ArrowRight size={16}/></a>
      </div>
    </section>
  </PublicLayout>
);

export const Industries = () => (
  <PublicLayout>
    <DarkHero
      eyebrow="Industries"
      title={<>Built for the brands that <span className="ft-serif text-[var(--ft-gold)]">shape their categories</span>.</>}
      subtitle="FLOWTALIS supports beverage launches across hospitality, fitness, retail, corporate, nonprofit, and influencer verticals."
    />
    <section className="py-24 bg-white">
      <div className="ft-container grid md:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="industries-grid">
        {INDUSTRIES.map((ind) => (
          <div key={ind.name} className="ft-card">
            <h3 className="font-semibold text-lg text-[var(--ft-black)]">{ind.name}</h3>
            <div className="ft-gold-rule my-3" />
            <p className="text-sm text-[var(--ft-muted)] leading-relaxed">{ind.desc}</p>
          </div>
        ))}
      </div>
    </section>
  </PublicLayout>
);

export const FAQsPage = () => {
  const [moqDescription, setMoqDescription] = useState(MOQ_DESCRIPTION_DEFAULT);
  useEffect(() => {
    api.get("/products").then((r) => {
      if (r.data?.moq_description) setMoqDescription(r.data.moq_description);
    }).catch(() => {});
  }, []);
  return (
    <PublicLayout>
      <DarkHero
        eyebrow="Answers"
        title={<>Frequently asked <span className="ft-serif text-[var(--ft-gold)]">questions</span>.</>}
        subtitle="Everything you need to know before launching your beverage brand with FLOWTALIS."
      />
      <section className="py-24 bg-white">
        <div className="ft-container max-w-3xl space-y-3">
          {FAQS.map((f, i) => (
            <details key={i} className="ft-card group" data-testid={`faq-${i}`}>
              <summary className="cursor-pointer list-none flex justify-between items-center font-semibold">
                {f.q}
                <span className="text-[var(--ft-gold)] group-open:rotate-45 transition-transform text-xl leading-none font-light">+</span>
              </summary>
              <p className="mt-4 text-sm text-[var(--ft-muted)] leading-relaxed">{resolveFaqAnswer(f.a, moqDescription)}</p>
            </details>
          ))}
        </div>
      </section>
    </PublicLayout>
  );
};

export const Services = () => (
  <PublicLayout>
    <DarkHero
      eyebrow="Services"
      title={<>End-to-end beverage development, <span className="ft-serif text-[var(--ft-gold)]">under one roof</span>.</>}
    />
    <section className="py-24 bg-white">
      <div className="ft-container grid md:grid-cols-2 lg:grid-cols-3 gap-5">
        {[
          { t: "Product Development", d: "Category selection, formulation, spec sheets, and compliance." },
          { t: "Brand & Artwork", d: "Label design, dielines, mockups, and pre-press files." },
          { t: "Manufacturing", d: "FDA-compliant partners for water, sparkling, and functional beverages." },
          { t: "Packaging & Bottling", d: "PET, aluminum, glass, and custom cap solutions." },
          { t: "Logistics & Shipping", d: "Palletized distribution, warehousing, and delivery coordination." },
          { t: "Client Success", d: "Dedicated launch team, portal, and reorder pipeline." },
        ].map((s) => (
          <div key={s.t} className="ft-card">
            <h3 className="font-semibold text-lg text-[var(--ft-black)]">{s.t}</h3>
            <div className="ft-gold-rule my-3" />
            <p className="text-sm text-[var(--ft-muted)] leading-relaxed">{s.d}</p>
          </div>
        ))}
      </div>
    </section>
  </PublicLayout>
);
